import { useDeferredValue } from "react";
import clsx from "clsx";

import { useBookmarks } from "lib/bookmarks/use-bookmarks";
import { serializeXpath } from "lib/navigation/paths";
import { ContentNode as Node } from "lib/types";

import css from "./content-node.module.scss";
import { Footnote } from "./footnote";
import { Image } from "./image";
import { Link } from "./link";

type ContentNodesProps = {
  content: Node[];
  firstChildMarginTop?: number;
};

export function ContentNodes({
  content,
  firstChildMarginTop,
}: ContentNodesProps) {
  const { bookmarks } = useBookmarks();
  const bookmarkedXpointers = new Set<string>(bookmarks.map(b => b.xpointer));
  const deferredBookmarkedXpointers = useDeferredValue(bookmarkedXpointers);

  return (
    <>
      {content.map((node, index) => (
        <ContentNode
          key={
            typeof node === "string" ? `text-${index}` : serializeXpath(node.xp)
          }
          node={node}
          bookmarkedXpointers={deferredBookmarkedXpointers}
          marginTop={index === 0 ? firstChildMarginTop : undefined}
        />
      ))}
    </>
  );
}

export type ContentNodeProps = {
  node: Node | string;
  bookmarkedXpointers: Set<string>;
  marginTop?: number;
};

export function ContentNode({
  node,
  bookmarkedXpointers,
  marginTop,
}: ContentNodeProps) {
  if (typeof node === "string") {
    return node;
  }

  const id = serializeXpath(node.xp);

  const children = node.c?.map((child, index) => (
    <ContentNode
      key={
        typeof child === "string" ? `text-${index}` : serializeXpath(child.xp)
      }
      node={child}
      bookmarkedXpointers={bookmarkedXpointers}
    />
  ));

  const isBookmarked = bookmarkedXpointers.has(id);

  const commonProps = {
    id,
    ...(marginTop && { style: { marginTop: `${marginTop}px` } }),
  };

  switch (node.t) {
    case "title":
      return (
        <h2
          key={id}
          {...commonProps}
          className={clsx(css.title, isBookmarked && css.bookmarked)}
        >
          {children}
        </h2>
      );
    case "subtitle":
      return (
        <h3
          key={id}
          {...commonProps}
          className={clsx(isBookmarked && css.bookmarked)}
        >
          {children}
        </h3>
      );
    case "p":
      return (
        <p
          key={id}
          {...commonProps}
          className={clsx(css.p, isBookmarked && css.bookmarked)}
        >
          {children}
        </p>
      );
    case "div":
      return (
        <div
          key={id}
          {...commonProps}
          className={clsx(
            isBookmarked && css.bookmarked,
            node.fl === "center" && css.center,
            node.op === "1" && css.onePiece
          )}
        >
          {children}
        </div>
      );
    case "br":
      return <br key={id} {...commonProps} />;
    case "img":
      return <Image key={id} {...commonProps} node={node} />;
    case "a":
      return (
        <Link key={id} {...commonProps} node={node}>
          {children}
        </Link>
      );
    case "note":
      return <Footnote key={id} {...commonProps} node={node} />;
    case "em":
      return (
        <em
          key={id}
          {...commonProps}
          className={clsx(isBookmarked && css.bookmarked)}
        >
          {children}
        </em>
      );
    case "strong":
      return (
        <strong
          key={id}
          {...commonProps}
          className={clsx(isBookmarked && css.bookmarked)}
        >
          {children}
        </strong>
      );
    case "strikethrough":
      return (
        <s
          key={id}
          {...commonProps}
          className={clsx(isBookmarked && css.bookmarked)}
        >
          {children}
        </s>
      );
    case "sub":
      return (
        <sub
          key={id}
          {...commonProps}
          className={clsx(isBookmarked && css.bookmarked)}
        >
          {children}
        </sub>
      );
    case "sup":
      return (
        <sup
          key={id}
          {...commonProps}
          className={clsx(isBookmarked && css.bookmarked)}
        >
          {children}
        </sup>
      );
    case "blockquote":
      return (
        <blockquote
          key={id}
          {...commonProps}
          className={clsx(isBookmarked && css.bookmarked)}
        >
          {children}
        </blockquote>
      );
    case "epigraph":
      return (
        <blockquote
          key={id}
          {...commonProps}
          className={clsx(css.epigraph, isBookmarked && css.bookmarked)}
        >
          {children}
        </blockquote>
      );
    case "subscription":
      return (
        <div
          key={id}
          {...commonProps}
          className={clsx(css.attribution, isBookmarked && css.bookmarked)}
        >
          {children}
        </div>
      );
    default:
      return (
        <div
          key={id}
          {...commonProps}
          className={clsx(isBookmarked && css.bookmarked)}
        >
          {children}
        </div>
      );
  }
}
